import { useEffect } from 'react';
import { AppDispatch } from 'common/store/store';
import {
  setStepNameUploadData,
  setCategoryData,
  setStepVehicleData,
  setStepPropertyData,
  setStepGroupData,
} from 'common/store/appSlice';
import { createCategoryKey } from 'common/utils/categoryUtils';
import { DefaultUpload } from 'common/store/types';

/**
 * "Upload preset" - the category (and group) a new document should land in
 * when the user starts an upload from inside a category or group page.
 *
 * ViewCategory / ViewGroup register their preset while they are mounted
 * (useUploadPreset) and clear it on unmount. Every "start a new upload"
 * entry point (startUploadFlow, which the bottom-nav "+" and the in-page
 * "Add a Doq" buttons both go through, and the desktop header "+") resets
 * the draft and then re-applies whatever preset is registered right now.
 * Leaving the page clears it, so an upload started later from the
 * dashboard begins with a genuinely empty draft.
 *
 * Kept in module memory on purpose, not in the (redux-persisted) app slice:
 * a preset is only meaningful while its page is on screen, and must never
 * survive a reload or a cold start.
 */
export type UploadPreset = {
  category: {
    id: number;
    isCustom: boolean;
    name?: string;
    slug?: string;
    image?: string;
    icon?: string;
    color?: string;
  };
  vehicle?: { id: number; name?: string; registration?: string };
  property?: { id: number; name?: string };
  // Generic (non vehicle/property) group: slug is the registry categorySlug.
  group?: { slug: string; id: number; name?: string };
};

let currentPreset: UploadPreset | null = null;

export const getUploadPreset = (): UploadPreset | null => currentPreset;

export const setUploadPreset = (preset: UploadPreset | null): void => {
  currentPreset = preset;
};

// Only clears when the registered preset is still the caller's own, so a
// page that unmounts after the next page has already registered (or a
// re-render that swapped in a fresh preset object) can't wipe someone
// else's preset.
export const clearUploadPreset = (preset: UploadPreset): void => {
  if (currentPreset === preset) currentPreset = null;
};

/**
 * Registers `preset` for as long as the calling page is mounted (or until
 * it changes). Pass null when the page has nothing to preset (still
 * loading, read-only, uncategorised). Callers should memoise the object.
 */
export const useUploadPreset = (preset: UploadPreset | null): void => {
  useEffect(() => {
    if (!preset) return undefined;
    setUploadPreset(preset);
    return () => clearUploadPreset(preset);
  }, [preset]);
};

/**
 * Writes a preset onto a freshly reset draft. Callers run this AFTER
 * resetUploadData() (+ the reminder-default seed), never on its own.
 */
export const applyUploadPreset = (dispatch: AppDispatch, preset: UploadPreset | null): void => {
  if (!preset) return;
  const { category } = preset;
  dispatch(setStepNameUploadData({
    name: '',
    category: [createCategoryKey(category.id, category.isCustom)],
    stepNameStatus: 1,
  } as DefaultUpload));
  // The category-picker screen is what normally fetches + dispatches
  // categoryList. It never mounts on a preset path, so seed the one record
  // we have; DocumentReview / GiveItName / GroupSelectionStep resolve the
  // picked category's name, icon and slug from this list.
  dispatch(setCategoryData({
    categoryList: [{
      id: category.id,
      name: category.name || '',
      slug: category.slug || '',
      image: category.image || '',
      is_custom: category.isCustom,
      icon: category.icon,
      color: category.color,
    }],
  }));
  if (preset.vehicle) {
    dispatch(setStepVehicleData({
      vehicleId: preset.vehicle.id,
      vehicleName: preset.vehicle.name,
      vehicleRegistration: preset.vehicle.registration,
      stepVehicleStatus: 1,
    }));
  }
  if (preset.property) {
    dispatch(setStepPropertyData({
      propertyId: preset.property.id,
      propertyName: preset.property.name,
    }));
  }
  if (preset.group) {
    dispatch(setStepGroupData({
      groupSlug: preset.group.slug,
      groupId: preset.group.id,
      groupName: preset.group.name,
    }));
  }
};
